import React, { useState, useEffect } from "react";
import { motion } from "framer-motion";
import { Line } from "react-chartjs-2";
import {
  Chart as ChartJS,
  LineElement,
  PointElement,
  LinearScale,
  CategoryScale,
  Title,
  Tooltip,
  Legend,
  Filler,
} from "chart.js";
import LeadModal from "../../../../features/landing/shared/LeadModal";
import LeadTracker from "../../../../features/landing/shared/LeadTracker";
import Navigation from "../../shared/Navigation"; 
import { useLocation } from "react-router-dom";
import { Helmet } from "react-helmet";

// Register Chart.js components
ChartJS.register(
  LineElement,
  PointElement,
  LinearScale,
  CategoryScale,
  Title,
  Tooltip,
  Legend,
  Filler
);

const SIPCalculator = () => {
  const [monthlyInvestment, setMonthlyInvestment] = useState(5000);
  const [years, setYears] = useState(10);
  const [expectedReturn, setExpectedReturn] = useState(12);
  const [result, setResult] = useState(null);
  const [chartData, setChartData] = useState(null);
  const [showLeadModal, setShowLeadModal] = useState(false);
  const [showEbookModal, setShowEbookModal] = useState(false);
  const [calculationDone, setCalculationDone] = useState(false);
  const location = useLocation();

  // Helper for INR formatting
  const formatINR = (value) =>
    value.toLocaleString("en-IN", { maximumFractionDigits: 0 });

  const calculateSIP = () => {
    const monthlyRate = expectedReturn / (12 * 100);
    const months = years * 12;
    const investedAmount = monthlyInvestment * months;

    const futureValue =
      monthlyInvestment *
      ((Math.pow(1 + monthlyRate, months) - 1) / monthlyRate) *
      (1 + monthlyRate);

    const wealthGained = futureValue - investedAmount;

    // Generate year-wise data for the chart
    const yearlyData = Array.from({ length: years + 1 }, (_, i) => {
      const monthsTillNow = i * 12;
      const investedSoFar = monthlyInvestment * monthsTillNow;
      const currentValue =
        monthlyInvestment *
        ((Math.pow(1 + monthlyRate, monthsTillNow) - 1) / monthlyRate) *
        (1 + monthlyRate);
      return {
        year: i,
        invested: investedSoFar,
        value: Math.round(currentValue),
      };
    });

    setChartData({
      labels: yearlyData.map((d) => `Year ${d.year}`),
      datasets: [
        {
          label: "Portfolio Value",
          data: yearlyData.map((d) => d.value),
          borderColor: "#6366f1",
          backgroundColor: "rgba(99, 102, 241, 0.1)",
          fill: true,
          tension: 0.3,
        },
        {
          label: "Invested Amount",
          data: yearlyData.map((d) => d.invested),
          borderColor: "#a5b4fc",
          backgroundColor: "rgba(165, 180, 252, 0.1)",
          fill: false,
          borderDash: [6, 6],
          tension: 0.3,
        },
      ],
    });

    setResult({
      futureValue: Math.round(futureValue),
      investedAmount,
      wealthGained: Math.round(wealthGained),
    });
  };

  useEffect(() => {
    // Parse query params on mount
    const params = new URLSearchParams(location.search);
    const qMonthly = params.get("monthlySipAmount");
    const qYears = params.get("durationInYears");
    let updated = false;
    if (qMonthly && !isNaN(Number(qMonthly))) {
      setMonthlyInvestment(Number(qMonthly));
      updated = true;
    }
    if (qYears && !isNaN(Number(qYears))) {
      setYears(Number(qYears));
      updated = true;
    }
    if (updated) {
      setExpectedReturn(12); // Always set to 12% if query params are used
    }
    // eslint-disable-next-line
  }, []);

  useEffect(() => {
    calculateSIP();
  }, [monthlyInvestment, years, expectedReturn]);

  const handleCalculate = () => {
    calculateSIP();
    setCalculationDone(true);
  };

  const handleDownloadOrPlan = (e) => {
    e.preventDefault(); // Prevent default button behavior
    setShowLeadModal(true);
    setCalculationDone(true);
  };

  return (
    <>
      <Helmet>
        <title>
          SIP Calculator - Systematic Investment Plan Returns | BuildMyWealth
        </title>
        <meta
          name="description"
          content="Use our SIP Calculator to estimate your future wealth, total investment, and returns from monthly SIPs. Plan your investments smartly with BuildMyWealth."
        />
        <meta
          name="keywords"
          content="SIP Calculator, Systematic Investment Plan, Mutual Fund Calculator, SIP Returns, Investment Planning, BuildMyWealth"
        />
        <link
          rel="canonical"
          href="https://www.buildmywealth.net/calculators/sip"
        />
      </Helmet>
      <Navigation />
      <div className="w-full max-w-3xl mx-auto py-6 pt-28 px-2 sm:px-4">
        <h1 className="text-2xl sm:text-3xl md:text-4xl font-bold text-center mb-8 bg-gradient-to-r from-indigo-600 to-purple-600 bg-clip-text text-transparent">
          SIP Calculator
        </h1>
        <div className="grid grid-cols-1 md:grid-cols-2 gap-6 md:gap-8">
          <motion.div
            className="space-y-4"
            initial={{ opacity: 0, x: -20 }}
            animate={{ opacity: 1, x: 0 }}
            transition={{ duration: 0.5 }}
          >
            <div>
              <label className="block text-xs font-medium text-gray-700 mb-1">
                Monthly Investment (₹)
              </label>
              <input
                type="range"
                min="500"
                max="100000"
                step="500"
                value={monthlyInvestment}
                onChange={(e) => setMonthlyInvestment(Number(e.target.value))}
                className="w-full h-2 bg-gray-200 rounded-lg appearance-none cursor-pointer accent-indigo-500"
              />
              <input
                type="number"
                value={monthlyInvestment}
                onChange={(e) => setMonthlyInvestment(Number(e.target.value))}
                className="mt-1 block w-full px-2 py-1 border border-gray-300 rounded-md shadow-sm focus:ring-indigo-500 focus:border-indigo-500 text-sm"
              />
            </div>
            <div>
              <label className="block text-xs font-medium text-gray-700 mb-1">
                Time Period (Years)
              </label>
              <input
                type="range"
                min="1"
                max="30"
                value={years}
                onChange={(e) => setYears(Number(e.target.value))}
                className="w-full h-2 bg-gray-200 rounded-lg appearance-none cursor-pointer accent-purple-500"
              />
              <input
                type="number"
                value={years}
                onChange={(e) => setYears(Number(e.target.value))}
                className="mt-1 block w-full px-2 py-1 border border-gray-300 rounded-md shadow-sm focus:ring-purple-500 focus:border-purple-500 text-sm"
              />
            </div>
            <div>
              <label className="block text-xs font-medium text-gray-700 mb-1">
                Expected Annual Returns (%)
              </label>
              <input
                type="range"
                min="1"
                max="30"
                value={expectedReturn}
                onChange={(e) => setExpectedReturn(Number(e.target.value))}
                className="w-full h-2 bg-gray-200 rounded-lg appearance-none cursor-pointer accent-green-500"
              />
              <input
                type="number"
                value={expectedReturn}
                onChange={(e) => setExpectedReturn(Number(e.target.value))}
                className="mt-1 block w-full px-2 py-1 border border-gray-300 rounded-md shadow-sm focus:ring-green-500 focus:border-green-500 text-sm"
              />
            </div>
          </motion.div>
          <motion.div
            className="space-y-3"
            initial={{ opacity: 0, x: 20 }}
            animate={{ opacity: 1, x: 0 }}
            transition={{ duration: 0.5 }}
          >
            {result && (
              <div className="grid grid-cols-1 gap-2">
                <div className="bg-gradient-to-br from-indigo-50 to-purple-50 p-3 rounded-lg">
                  <p className="text-xs text-gray-600">Future Value</p>
                  <p className="text-lg font-bold text-indigo-600">
                    ₹{formatINR(result.futureValue)}
                  </p>
                </div>
                <div className="bg-gradient-to-br from-indigo-50 to-purple-50 p-3 rounded-lg">
                  <p className="text-xs text-gray-600">Total Investment</p>
                  <p className="text-lg font-bold text-indigo-600">
                    ₹{formatINR(result.investedAmount)}
                  </p>
                </div>
                <div className="bg-gradient-to-br from-indigo-50 to-purple-50 p-3 rounded-lg">
                  <p className="text-xs text-gray-600">Wealth Gained</p>
                  <p className="text-lg font-bold text-indigo-600">
                    ₹{formatINR(result.wealthGained)}
                  </p>
                </div>
              </div>
            )}
          </motion.div>
        </div>

        <div className="mt-4 text-center">
          <a
            href="https://wa.me/919616622620?text=Hi%2C%20I%20need%20help%20with%20SIP%20planning%20on%20BuildMyWealth.net"
            target="_blank"
            rel="noopener noreferrer"
            className="bg-green-500 text-white px-4 py-2 rounded-md font-semibold hover:bg-green-600 inline-block"
            style={{ textDecoration: "none" }}
          >
            <span role="img" aria-label="WhatsApp" className="mr-2">
              💬
            </span>
            Get Expert Help on WhatsApp
          </a>
        </div>
        {chartData && (
          <motion.div
            className="mt-4 bg-white rounded-lg shadow p-2 sm:p-4 overflow-x-auto"
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.5, delay: 0.2 }}
          >
            <Line
              data={chartData}
              options={{
                responsive: true,
                maintainAspectRatio: false,
                plugins: {
                  legend: {
                    position: "top",
                    labels: { font: { size: 12 } },
                  },
                  title: {
                    display: true,
                    text: "Portfolio Growth Projection",
                    font: { size: 16 },
                  },
                },
                scales: {
                  y: {
                    beginAtZero: true,
                    ticks: {
                      callback: (value) => `₹${formatINR(value)}`,
                      font: { size: 12 },
                    },
                  },
                  x: {
                    ticks: { font: { size: 12 } },
                  },
                },
              }}
              height={260}
            />
          </motion.div>
        )}
        <LeadModal
          isVisible={showLeadModal}
          onClose={() => setShowLeadModal(false)}
        >
          <LeadTracker />
        </LeadModal>
        <LeadModal
          isVisible={showEbookModal}
          onClose={() => setShowEbookModal(false)}
        >
          <div className="p-4">
            <h4 className="text-lg font-bold mb-2 text-indigo-700">
              Get Your Free eBook
            </h4>
            <p className="text-sm text-gray-600 mb-4">
              Enter your email or phone to receive "5 Steps to Start Your First
              SIP" instantly.
            </p>
            <LeadTracker />
          </div>
        </LeadModal>
      </div> 
    </>
  );
};

export default SIPCalculator;
